import { useEffect, useState } from "react";
import { useBrandingContext } from "../../../../../components/BrandingContext";
import { useHotelInfoContext } from "../../../../../components/HotelInfoContext";
import { mockPromotions } from "../../const";

export const useConfirmation = (location, navigate) => {
  const [booking, setBooking] = useState(null);
  const [hotelInfo, setHotelInfo] = useState(null);
  const [branding, setBranding] = useState(null);
  const [extraBedPrice, setExtraBedPrice] = useState(null);
  const [hotelRules, setHotelRules] = useState(null);

  const { hotelInfo: ctxHotelInfo } = useHotelInfoContext() || {
    hotelInfo: null,
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const orderId = params.get("orderId");

    if (!location.state || !location.state.booking) {
      const fetchBookingDetails = async () => {
        try {
          const bookingResponse = await fetch(
            `/api/bookings/getBookingById?orderId=${orderId}`
          );
          const bookingData = await bookingResponse.json();
          setBooking(bookingData.booking);
          // hotelInfo will come from HotelInfoProvider; if available set it
          if (ctxHotelInfo) setHotelInfo(ctxHotelInfo);
        } catch (error) {
          console.error("Error fetching booking details:", error);
          navigate("/");
        }
      };
      if (orderId) fetchBookingDetails();
      else navigate("/");
    } else {
      setBooking(location.state.booking);
      setHotelInfo(location.state.hotelInfo || ctxHotelInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // Keep hotelInfo up-to-date when provider updates
  useEffect(() => {
    if (!hotelInfo && ctxHotelInfo) setHotelInfo(ctxHotelInfo);
  }, [ctxHotelInfo, hotelInfo]);

  const { branding: ctxBranding } = useBrandingContext();

  useEffect(() => {
    if (location.state?.branding) {
      setBranding(location.state.branding);
      return;
    }
    if (ctxBranding) setBranding(ctxBranding);
  }, [location.state?.branding, ctxBranding]);

  useEffect(() => {
    const fetchExtraBedPrice = async () => {
      try {
        const response = await fetch("/api/extrabedprice/get");
        const data = await response.json();
        setExtraBedPrice(data.price);
      } catch (error) {
        console.error("Error fetching extra bed price:", error);
      }
    };

    fetchExtraBedPrice();
  }, []);

  useEffect(() => {
    const fetchHotelRules = async () => {
      try {
        const response = await fetch("/api/hotelrules/get");
        const rulesData = await response.json();
        setHotelRules(rulesData);
      } catch (error) {
        console.error("Error fetching hotel rules:", error);
      }
    };
    fetchHotelRules();
  }, []);

  // Validate rateBreakdown against ratePlanRate and promotions
  useEffect(() => {
    if (booking && booking.rooms) {
      booking.rooms.forEach((room, index) => {
        if (Array.isArray(room.rateBreakdown)) {
          // Check rate mismatch
          const totalFinalRate = room.rateBreakdown.reduce(
            (sum, rate) => sum + (rate.finalRate || rate.originalRate),
            0
          );
          if (Math.abs(totalFinalRate - room.ratePlanRate) > 1) {
            // Allow small rounding errors
            console.warn(
              `Rate mismatch for Room ${index + 1}: ${room.room} - ${
                room.ratePlanName
              }. ` +
                `ratePlanRate: ${room.ratePlanRate}, Sum of finalRate: ${totalFinalRate}`
            );
          }
          // Check promotion applicability
          room.rateBreakdown.forEach((rate, rateIndex) => {
            if (rate.promotion && rate.promotion !== "No Promotion") {
              const isValidPromotion = mockPromotions.some(
                (promo) =>
                  promo.name === rate.promotion &&
                  promo.applicableRatePlans.includes(room.ratePlanName)
              );
              if (!isValidPromotion) {
                console.warn(
                  `Invalid promotion for Room ${index + 1}: ${room.room} - ${
                    room.ratePlanName
                  }. ` +
                    `Promotion: ${rate.promotion}, Date: ${rate.date}, Final Rate: ${rate.finalRate}`
                );
              }
              // Check if finalRate matches expected discount
              const expectedFinalRate =
                rate.originalRate *
                (1 - (rate.promotionDiscount || 0) / 100) *
                (room.promoCode && rate.promoCode === room.promoCode ? 0.5 : 1); // Assuming Chau50 is 50%
              if (Math.abs(rate.finalRate - expectedFinalRate) > 1) {
                console.warn(
                  `Final rate mismatch for Room ${index + 1}: ${room.room} - ${
                    room.ratePlanName
                  }, ` +
                    `Date: ${rate.date}. Expected: ${expectedFinalRate}, Actual: ${rate.finalRate}`
                );
              }
            }
          });
        }
      });
    }
  }, [booking]);

  return {
    booking,
    hotelInfo,
    branding,
    extraBedPrice,
    hotelRules,
  };
};
